// apolo/src/components/code/sessionWorkspace/autoCompactGate.js — harness-live-verify E3.
// Wiring do auto-compact no runAgent (fora do spine ratcheado): mede o pct do histórico
// MODEL-FACING (buildAgentHistory reseta no marker — medir o feed inteiro thrasharia por
// construção), decide via máquina pura (lib/loop/autoCompact.js) e insere o marker pelo
// MESMO caminho do /compact (pushMsg no feed + persistMessages). Flag apolo_auto_compact
// PROVADA AO VIVO 2026-07-14 (marker no ~88%, histórico resetou, turnos voltaram a caber)
// ⇒ DEFAULT ON; kill-switch '0'/'false' devolve a MESMA ref/false (byte-idêntico).

import { computeContextUsage } from '../../../lib/contextUsage.js';
import { buildAgentHistory } from '../../../lib/agentHistory.js';
import { buildCompactSummary, compactMarkerMessage } from '../../../lib/compactContext.js';
import { condenserSummaryIfEnabled } from '../../../lib/loop/wire/condenserGate.js';
import { persistMessages } from '../../../lib/persistMessages.js';
import { readFlagDefaultOn } from '../../../lib/flags/sprintFlags.js';
import {
  evaluateAutoCompact, initialGateState, countUserTurns, thrashNoticeMessage,
} from '../../../lib/loop/autoCompact.js';

const FLAG = 'apolo_auto_compact';

// Estado por sessão (sobrevive a remounts; morre com o reload — igual agentRunStore).
const bySession = new Map();
function stateFor(sessionId) {
  let st = bySession.get(sessionId);
  if (!st) {
    st = { gate: initialGateState(), overflowRetryUsed: false };
    bySession.set(sessionId, st);
  }
  return st;
}

/** Test-only: zera o estado de todas as sessões. */
export function resetAutoCompactState() {
  bySession.clear();
}

function insertMarker(sessionId, liveMessages, pushMsg) {
  // E2 condenser (flag apolo_condenser, default-OFF): partial-condensation summary
  // when ON, else the legacy deterministic digest (byte-identical). Never throws.
  const summary = condenserSummaryIfEnabled(liveMessages) ?? buildCompactSummary(liveMessages);
  const marker = compactMarkerMessage(summary);
  pushMsg(marker);
  persistMessages(sessionId, [marker]).catch(() => {});
  return marker;
}

/**
 * Pré-send (E3-WS1-T01/T03): decide compactar/parar e devolve as liveMessages a usar
 * no build do histórico (com o marker appendado quando compactou). `fresh` (turno novo
 * do usuário) rearma o retry de overflow. Flag OFF ⇒ mesma ref (byte-idêntico).
 * @param {{ sessionId:string, liveMessages:Array, model:string, pushMsg:Function,
 *           fresh?:boolean, usageFn?:Function }} p
 * @returns {Array}
 */
export function autoCompactBeforeSend({ sessionId, liveMessages, model, pushMsg, fresh = false, usageFn = computeContextUsage }) {
  if (!readFlagDefaultOn(FLAG)) return liveMessages;
  const st = stateFor(sessionId);
  if (fresh) st.overflowRetryUsed = false;
  const { pct } = usageFn(buildAgentHistory(liveMessages), model);
  const { action, state } = evaluateAutoCompact(st.gate, {
    pct,
    userTurns: countUserTurns(liveMessages),
    msgCount: liveMessages.length,
    now: Date.now(),
  });
  st.gate = state;
  if (action === 'stop') {
    const notice = thrashNoticeMessage();
    pushMsg(notice);
    persistMessages(sessionId, [notice]).catch(() => {});
    return liveMessages;
  }
  if (action !== 'compact') return liveMessages;
  const marker = insertMarker(sessionId, liveMessages, pushMsg);
  return [...liveMessages, marker];
}

/**
 * Pós-run (E3-WS1-T02): stopReason 'context_overflow' ⇒ insere o marker e manda o
 * caller RETENTAR 1x (true). Uma vez por turno de usuário (rearma no pré-send fresh);
 * gate parado por thrash não retenta. Flag OFF ⇒ false (byte-idêntico).
 * @param {{ sessionId:string, agentResult:object, liveMessages:Array, pushMsg:Function }} p
 * @returns {boolean} true = compactou; o caller deve retentar o round
 */
export function compactOnOverflow({ sessionId, agentResult, liveMessages, pushMsg }) {
  if (agentResult?.stopReason !== 'context_overflow') return false;
  if (!readFlagDefaultOn(FLAG)) return false;
  const st = stateFor(sessionId);
  if (st.overflowRetryUsed || st.gate.stopped) return false;
  st.overflowRetryUsed = true;
  insertMarker(sessionId, liveMessages, pushMsg);
  return true;
}
